import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Link, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  AssessmentIdSchema,
  fullAssessmentOf,
  type AssessmentId,
  type FullAssessmentView,
  type FullFilter,
  type SharedAssessment,
} from "@thalamos/haven-common";

import guestApi from "../../api/guest/api.js";
import { FullAssessment } from "../../components/FullAssessment/FullAssessment.js";
import { guestRoute } from "../../routing/guestRoute.js";
import { buildForm } from "../Assessment/AssessmentContext.js";
import type { PdfDownloadOutcome } from "../Assessment/assessmentPdfDownload.js";
import { latestSubmission } from "../Assessment/latestSubmission.js";
import { GuestBrandedPage } from "./GuestAssessmentLayout.js";
import { downloadShared } from "./GuestSharedPage.js";

const { guest } = guestApi;

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

/**
 * Reading one shared assessment: under way, shown, not shared with this
 * guest (or not an assessment at all), ended (the session no longer admits
 * the guest), or failed for another reason.
 */
export type SharedAssessmentReading =
  | { kind: "reading" }
  | {
      kind: "shown";
      assessment: SharedAssessment["assessment"];
      view: FullAssessmentView;
    }
  | { kind: "not_shared" }
  | { kind: "ended" }
  | { kind: "failed" };

const readSharedAssessment = async (
  assessmentId: AssessmentId,
): Promise<SharedAssessmentReading> => {
  const opened = await guest.sharedAssessment(assessmentId);

  if (opened.status === 401) {
    return { kind: "ended" };
  }

  if (opened.status === 404) {
    return { kind: "not_shared" };
  }

  if (opened.data === undefined) {
    return { kind: "failed" };
  }

  const { assessment } = opened.data;
  const form = await buildForm({
    name: assessment.instrument.name,
    digest: assessment.instrument.digest,
    instrumentSchema: () => guest.sharedInstrument(assessmentId),
  });
  const submission = latestSubmission(assessment);

  return form === "unavailable" || submission === undefined
    ? { kind: "failed" }
    : {
        kind: "shown",
        assessment,
        view: fullAssessmentOf({
          schema: form.schema,
          form: form.form,
          responses: submission.responses,
        }),
      };
};

/**
 * One assessment shared with a guest, in the Full assessment's layout
 * without what a practitioner has beside it: domains open and close, and each
 * item shows the rating given, its words and the comment.
 */
export const GuestSharedAssessmentPage = () => {
  const { assessmentId } = useParams();
  const parsedId = AssessmentIdSchema.safeParse(assessmentId).data;
  const [read, setRead] = useState<SharedAssessmentReading>({
    kind: "reading",
  });

  const readAgain = useCallback(async () => {
    if (parsedId === undefined) {
      setRead({ kind: "not_shared" });
      return;
    }

    setRead(
      await readSharedAssessment(parsedId).catch(
        (): SharedAssessmentReading => ({
          kind: "failed",
        }),
      ),
    );
  }, [parsedId]);

  useEffect(() => {
    void readAgain();
  }, [readAgain]);

  return (
    <GuestSharedAssessmentContent
      read={read}
      onRetry={readAgain}
      onDownload={downloadShared}
    />
  );
};

/** A shared assessment's rendering, with its layout held on the page. */
export const GuestSharedAssessmentContent = ({
  read,
  onRetry,
  onDownload,
}: {
  read: SharedAssessmentReading;
  onRetry: () => Promisable<void>;
  onDownload: (assessmentId: AssessmentId) => Promisable<PdfDownloadOutcome>;
}) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FullFilter>("all");
  const [fullText, setFullText] = useState(false);
  const [open, setOpen] = useState<readonly string[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const back = (
    <Link component={RouterLink} to={guestRoute.shared()}>
      {t("guestShared.back")}
    </Link>
  );

  return (
    <GuestBrandedPage offersSignOut={read.kind !== "ended"}>
      {match(read)
        .with({ kind: "reading" }, () => (
          <Typography color="text.secondary">{t("common.loading")}</Typography>
        ))
        .with({ kind: "ended" }, () => (
          <Stack spacing={1}>
            <Typography variant="h4" component="h1">
              {t("guestAccess.sessionEnded.heading")}
            </Typography>
            <Typography>{t("guestAccess.sessionEnded.body")}</Typography>
          </Stack>
        ))
        .with({ kind: "not_shared" }, () => (
          <Stack spacing={1} alignItems="flex-start">
            <Alert severity="info">{t("guestShared.notFound")}</Alert>
            {back}
          </Stack>
        ))
        .with({ kind: "failed" }, () => (
          <Stack spacing={1} alignItems="flex-start">
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void onRetry()}
                >
                  {t("guestShared.retry")}
                </Button>
              }
            >
              {t("assessment.full.formUnavailable")}
            </Alert>
            {back}
          </Stack>
        ))
        .with({ kind: "shown" }, ({ assessment, view }) => {
          const latest = latestSubmission(assessment);
          const finishedAt = latest?.decision?.decidedAt ?? latest?.submittedAt;

          return (
            <Stack spacing={2}>
              {back}
              <Stack
                direction="row"
                spacing={2}
                justifyContent="space-between"
                alignItems="flex-start"
                useFlexGap
                flexWrap="wrap"
              >
                <Box>
                  <Typography variant="h4" component="h1">
                    {assessment.definitive
                      ? t("guestShared.definitive")
                      : t("guestShared.nonDefinitive")}
                  </Typography>
                  {finishedAt === undefined ||
                  (assessment.status !== "approved" &&
                    assessment.status !== "completed") ? null : (
                    <Typography color="text.secondary">
                      {t(`guestShared.finished.${assessment.status}`, {
                        date: CALENDAR_DATE.format(finishedAt),
                      })}
                    </Typography>
                  )}
                </Box>
                <Stack spacing={1} alignItems="flex-end">
                  <Button
                    variant="outlined"
                    disabled={downloading}
                    onClick={() => {
                      setDownloading(true);
                      setDownloadFailed(false);
                      void Promise.resolve(assessment.id)
                        .then(onDownload)
                        .catch((): PdfDownloadOutcome => "failed")
                        .then((outcome) => {
                          setDownloading(false);
                          setDownloadFailed(outcome === "failed");
                        });
                    }}
                  >
                    {t("guestShared.download")}
                  </Button>
                  {downloadFailed ? (
                    <Alert severity="error">
                      {t("guestShared.downloadFailed")}
                    </Alert>
                  ) : null}
                </Stack>
              </Stack>
              <Box sx={{ p: 2, bgcolor: "#F7F8FA" }}>
                <FullAssessment
                  view={view}
                  filter={filter}
                  fullText={fullText}
                  open={new Set(open)}
                  onFilter={setFilter}
                  onFullText={setFullText}
                  onOpen={setOpen}
                  offersAllRatings={false}
                />
              </Box>
            </Stack>
          );
        })
        .exhaustive()}
    </GuestBrandedPage>
  );
};
